import { Component, EventEmitter, Input, OnInit, Output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import {
  ExpectedDelivery,
  ExpectedDeliveryItem,
  HANDLING_INSTRUCTIONS,
  ReleaseWithReceiptsParams,
  ReleaseItemParams
} from '../../../_models/expected-delivery.interface';
import { ExpectedDeliveryService } from '../../../_services/expected-delivery.service';
import { ProjectService, CustomerService, ArticleTemplateService, TicketService, UserService, SessionService, WarehouseService } from '../../../_services';
import { APProject } from '../../../_models/project.interface';
import { APCustomer } from '../../../_models/customer.interface';
import { ArticleTemplate, StorageBin } from '../../../_models';
import { ArticleTemplatePickerComponent } from '../../_shared/article-template-picker';

interface ItemFormData {
  id: number;
  displayDescription: string;
  customDescription: string | null;
  quantityOrdered: number;
  serialNumbersRaw: string[] | null;
  articleTemplateId: number | null;
  articleTemplateName: string | null;
  articleTemplateSn: boolean | null;
  owner: 'BENTOMAX' | 'CUSTOMER';
  storageBinId: number | null;
  storageBinLabel: string | null;
}

@Component({
  selector: 'app-expected-delivery-approval-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDatepickerModule, MatNativeDateModule, ArticleTemplatePickerComponent],
  templateUrl: './expected-delivery-approval-dialog.component.html'
})
export class ExpectedDeliveryApprovalDialogComponent implements OnInit {
  @Input() delivery!: ExpectedDelivery;
  /** Optionale externe Artikelvorlagen-Liste (vermeidet erneutes Laden) */
  @Input() externalArticleTemplates: ArticleTemplate[] | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() approved = new EventEmitter<ExpectedDelivery>();
  /** Emittiert neu geladene Templates (nach Reload im Picker) */
  @Output() templatesReloaded = new EventEmitter<ArticleTemplate[]>();

  // Wizard State
  currentStep = signal<number>(1);
  readonly totalSteps = 2;

  // Stammdaten (Step 1)
  handlingInstruction = signal<string>('');
  showCustomHandlingInstruction = signal<boolean>(false);
  customHandlingInstruction = signal<string>('');
  orderNr = signal<string>('');
  ticketNumber = signal<string>('');
  ticketCheckStatus = signal<'idle' | 'checking' | 'found' | 'not_found'>('idle');
  foundTicketId = signal<number | null>(null);
  projectId = signal<number | null>(null);
  customerId = signal<number | null>(null);
  expectedFrom = signal<Date | null>(null);
  expectedUntil = signal<Date | null>(null);
  internalNotificationEmails = signal<string>('');
  externalNotificationEmails = signal<string>('');
  packagingType = signal<string>('');
  notes = signal<string>('');

  // Items (Step 2)
  items = signal<ItemFormData[]>([]);

  // Status
  isSubmitting = signal<boolean>(false);
  errorMessage = signal<string>('');

  // Referenzdaten
  projects: APProject[] = [];
  customers: APCustomer[] = [];
  articleTemplates: ArticleTemplate[] = [];
  filteredTemplates = signal<ArticleTemplate[]>([]);

  // Template Modal
  showTemplateModal = signal<boolean>(false);
  templateModalItemIndex = signal<number>(-1);
  templateSearchQuery = signal<string>('');
  filterByProject = signal<boolean>(false);
  filterByCustomer = signal<boolean>(false);
  templateDisplayLimit = signal<number>(30);
  readonly TEMPLATE_PAGE_SIZE = 30;

  // Storage Bins
  storageBins: StorageBin[] = [];
  showStorageBinModal = signal<boolean>(false);
  storageBinModalItemIndex = signal<number>(-1);
  storageBinSearchQuery = signal<string>('');
  readonly MAIN_WAREHOUSE_ID = 40;

  // Admin User Modal
  showAdminUserModal = signal<boolean>(false);
  adminUsers: { id: number; name: string; email: string; selected: boolean }[] = [];
  adminUsersLoaded = false;

  // Handling Instructions Options
  handlingInstructionOptions = [
    { value: HANDLING_INSTRUCTIONS.STOCK_IN, label: 'Einlagerung ins Hauptlager' },
    { value: HANDLING_INSTRUCTIONS.FORWARD, label: 'Weiterversand (an Techniker/Kunden)' },
    { value: 'OTHER', label: 'Andere (Freitext)' }
  ];

  // Computed: Step 1 valid
  isStep1Valid = computed(() => {
    const hi = this.handlingInstruction();
    return !!hi && hi.trim().length > 0;
  });

  // Computed: All items have templates
  allItemsHaveTemplates = computed(() => {
    return this.items().every(item => item.articleTemplateId !== null);
  });

  // Computed: Items without templates count
  itemsWithoutTemplateCount = computed(() => {
    return this.items().filter(item => item.articleTemplateId === null).length;
  });

  // Computed: All items have storage bins
  allItemsHaveStorageBins = computed(() => {
    return this.items().every(item => item.storageBinId !== null);
  });

  // Computed: Items without storage bins count
  itemsWithoutStorageBinCount = computed(() => {
    return this.items().filter(item => item.storageBinId === null).length;
  });

  // Computed: Items with missing serial numbers (SN-pflichtige Templates)
  itemsWithMissingSerialNumbers = computed(() => {
    return this.items().filter(item => {
      if (!item.articleTemplateSn) return false;
      const snCount = item.serialNumbersRaw?.length || 0;
      return snCount < item.quantityOrdered;
    });
  });

  // Computed: Can approve
  canApprove = computed(() => {
    return this.isStep1Valid() && this.allItemsHaveTemplates() && this.allItemsHaveStorageBins() && !this.isSubmitting();
  });

  // Computed: Visible templates (for pagination)
  visibleTemplates = computed(() => {
    return this.filteredTemplates().slice(0, this.templateDisplayLimit());
  });

  hasMoreTemplates = computed(() => {
    return this.filteredTemplates().length > this.templateDisplayLimit();
  });

  constructor(
    private expectedDeliveryService: ExpectedDeliveryService,
    private projectService: ProjectService,
    private customerService: CustomerService,
    private articleTemplateService: ArticleTemplateService,
    private ticketService: TicketService,
    private userService: UserService,
    private sessionService: SessionService,
    private warehouseService: WarehouseService
  ) {}

  ngOnInit(): void {
    this.initFromDelivery();
    this.loadData();
  }

  private initFromDelivery(): void {
    // Stammdaten aus Delivery übernehmen
    this.orderNr.set(this.delivery.order_nr || '');
    this.projectId.set(this.delivery.project_id);
    this.customerId.set(this.delivery.customer_id || null);
    this.packagingType.set(this.delivery.packaging_type || '');
    this.notes.set(this.delivery.notes || '');
    this.internalNotificationEmails.set(this.delivery.internal_notification_emails || '');
    this.externalNotificationEmails.set(this.delivery.external_notification_emails || '');

    if (this.delivery.expected_from) {
      this.expectedFrom.set(new Date(this.delivery.expected_from));
    }
    if (this.delivery.expected_until) {
      this.expectedUntil.set(new Date(this.delivery.expected_until));
    }

    // Handling Instruction
    if (this.delivery.handling_instruction) {
      const predefined = this.handlingInstructionOptions.find(
        o => o.value === this.delivery.handling_instruction
      );
      if (predefined && predefined.value !== 'OTHER') {
        this.handlingInstruction.set(this.delivery.handling_instruction);
      } else if (this.delivery.handling_instruction) {
        this.showCustomHandlingInstruction.set(true);
        this.customHandlingInstruction.set(this.delivery.handling_instruction);
        this.handlingInstruction.set(this.delivery.handling_instruction);
      }
    }

    // Items initialisieren
    const itemsData: ItemFormData[] = (this.delivery.items || []).map(item => ({
      id: item.id!,
      displayDescription: item.display_description || item.custom_description || '',
      customDescription: item.custom_description || null,
      quantityOrdered: item.quantity_ordered,
      serialNumbersRaw: item.serial_numbers_raw || null,
      articleTemplateId: item.article_template_id,
      articleTemplateName: item.article_template_description || null,
      articleTemplateSn: item.article_template_sn || null,
      owner: item.owner,
      storageBinId: null,
      storageBinLabel: null
    }));
    this.items.set(itemsData);

    // Default internal notification emails
    if (!this.internalNotificationEmails()) {
      const currentUser = this.sessionService.getCurrentUser();
      if (currentUser?.email1) {
        this.internalNotificationEmails.set(currentUser.email1);
      }
    }
  }

  private loadData(): void {
    this.projectService.getAPProjects().subscribe({
      next: (data) => this.projects = data.filter(p => p.active),
      error: (err) => console.error('Fehler beim Laden der Projekte:', err)
    });

    this.customerService.loadAPCustomers().subscribe({
      next: (data) => this.customers = data,
      error: (err) => console.error('Fehler beim Laden der Kunden:', err)
    });

    // Artikelvorlagen nur laden wenn nicht extern übergeben
    if (this.externalArticleTemplates && this.externalArticleTemplates.length > 0) {
      this.articleTemplates = this.externalArticleTemplates;
      this.filteredTemplates.set([...this.externalArticleTemplates]);
    } else {
      this.articleTemplateService.all().subscribe({
        next: (data) => {
          this.articleTemplates = data;
          this.filteredTemplates.set([...data]);
        },
        error: (err) => console.error('Fehler beim Laden der Artikelvorlagen:', err)
      });
    }

    // Storage Bins vom Hauptlager laden
    this.warehouseService.loadStorageBins(this.MAIN_WAREHOUSE_ID, { per_page: 500 }).subscribe({
      next: (data) => this.storageBins = data,
      error: (err) => console.error('Fehler beim Laden der Lagerplätze:', err)
    });
  }

  // Lädt Templates neu (für den Aktualisieren-Button)
  reloadTemplates(): void {
    this.articleTemplateService.all().subscribe({
      next: (data) => {
        this.articleTemplates = data;
        this.applyTemplateFilters();
      },
      error: (err) => console.error('Fehler beim Laden der Artikelvorlagen:', err)
    });
  }

  // === Navigation ===

  nextStep(): void {
    if (this.currentStep() === 1 && this.isStep1Valid()) {
      this.currentStep.set(2);
      this.errorMessage.set('');
    }
  }

  previousStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update(s => s - 1);
      this.errorMessage.set('');
    }
  }

  close(): void {
    this.closed.emit();
  }

  // === Handling Instruction ===

  onHandlingInstructionChange(value: string): void {
    if (value === 'OTHER') {
      this.showCustomHandlingInstruction.set(true);
      this.handlingInstruction.set('');
    } else {
      this.showCustomHandlingInstruction.set(false);
      this.customHandlingInstruction.set('');
      this.handlingInstruction.set(value);
    }
  }

  onCustomHandlingInstructionChange(value: string): void {
    this.customHandlingInstruction.set(value);
    this.handlingInstruction.set(value);
  }

  // === Ticket Lookup ===

  checkTicket(): void {
    const ticketNr = this.ticketNumber().trim();

    if (!ticketNr) {
      this.ticketCheckStatus.set('idle');
      this.foundTicketId.set(null);
      return;
    }

    this.ticketCheckStatus.set('checking');

    this.ticketService.findTicketGroupByOrderNr(ticketNr).subscribe({
      next: (response) => {
        this.ticketCheckStatus.set('found');
        this.foundTicketId.set(response.ticket.id);

        // Projekt automatisch auswählen
        if (response.ticket.project_id) {
          const projectId = Number(response.ticket.project_id);
          const project = this.projects.find(p => p.id === projectId);
          if (project) {
            this.projectId.set(projectId);
            if (project.customer?.id) {
              this.customerId.set(project.customer.id);
              this.updateItemOwners();
            }
          }
        }
      },
      error: () => {
        this.ticketCheckStatus.set('not_found');
        this.foundTicketId.set(null);
      }
    });
  }

  clearTicket(): void {
    this.ticketNumber.set('');
    this.ticketCheckStatus.set('idle');
    this.foundTicketId.set(null);
  }

  // === Project/Customer ===

  onProjectChange(projectId: number | null): void {
    this.projectId.set(projectId);
    if (projectId) {
      const project = this.projects.find(p => p.id === projectId);
      if (project?.customer?.id) {
        this.customerId.set(project.customer.id);
        this.updateItemOwners();
      }
    }
  }

  onCustomerChange(customerId: number | null): void {
    this.customerId.set(customerId);
    this.updateItemOwners();
  }

  private updateItemOwners(): void {
    const hasCustomer = !!this.customerId();
    const newOwner = hasCustomer ? 'CUSTOMER' : 'BENTOMAX';
    this.items.update(items => items.map(item => ({
      ...item,
      owner: newOwner as 'BENTOMAX' | 'CUSTOMER'
    })));
  }

  // === Template Selection Modal ===

  openTemplateModal(itemIndex: number): void {
    this.templateModalItemIndex.set(itemIndex);

    // Item-Beschreibung als initiale Suche setzen
    const item = this.items()[itemIndex];
    const initialSearch = item?.displayDescription || item?.customDescription || '';
    this.templateSearchQuery.set(initialSearch);

    // Kundenfilter aktivieren wenn Kunde gesetzt
    this.filterByProject.set(false);
    this.filterByCustomer.set(!!this.customerId());

    this.applyTemplateFilters();
    this.showTemplateModal.set(true);
  }

  closeTemplateModal(): void {
    this.showTemplateModal.set(false);
    this.templateModalItemIndex.set(-1);
  }

  /**
   * Gibt die initiale Suchanfrage für den Template-Picker zurück
   */
  getTemplateModalInitialSearch(): string {
    const index = this.templateModalItemIndex();
    if (index >= 0 && index < this.items().length) {
      const item = this.items()[index];
      return item?.displayDescription || item?.customDescription || '';
    }
    return '';
  }

  /**
   * Wird aufgerufen wenn ein Template im Picker ausgewählt wird
   */
  onTemplatePickerSelected(template: ArticleTemplate): void {
    this.selectTemplate(template);
  }

  /**
   * Leitet das Reload-Event vom Picker weiter und aktualisiert die lokale Liste
   */
  onPickerTemplatesReloaded(templates: ArticleTemplate[]): void {
    this.articleTemplates = templates;
    this.filteredTemplates.set([...templates]);
    this.templatesReloaded.emit(templates);
  }

  selectTemplate(template: ArticleTemplate): void {
    const index = this.templateModalItemIndex();
    if (index >= 0 && index < this.items().length) {
      this.items.update(items => {
        const updated = [...items];
        const item = updated[index];
        updated[index] = {
          ...item,
          articleTemplateId: template.id,
          articleTemplateName: template.description,
          articleTemplateSn: template.sn || false,
          // Bei neuen Positionen die Beschreibung vom Template übernehmen
          displayDescription: item.displayDescription || template.description
        };
        return updated;
      });
    }
    this.closeTemplateModal();
  }

  applyTemplateFilters(): void {
    let filtered = [...this.articleTemplates];

    // Text search
    const query = this.templateSearchQuery().toLowerCase();
    if (query.trim()) {
      filtered = filtered.filter(t =>
        t.description?.toLowerCase().includes(query) ||
        t.part_nr?.toLowerCase().includes(query) ||
        t.vendor?.toLowerCase().includes(query)
      );
    }

    // Filter by project
    if (this.filterByProject()) {
      const projId = this.projectId();
      if (projId) {
        filtered = filtered.filter(t => {
          if (!t.comment2) return false;
          const projectIds = t.comment2.split(',').map(id => id.trim());
          return projectIds.includes(String(projId));
        });
      }
    }

    // Filter by customer
    if (this.filterByCustomer()) {
      const custId = this.customerId();
      if (custId) {
        filtered = filtered.filter(t => {
          if (!t.comment3) return false;
          return t.comment3.trim() === String(custId);
        });
      }
    }

    this.filteredTemplates.set(filtered);
    this.templateDisplayLimit.set(this.TEMPLATE_PAGE_SIZE);
  }

  onTemplateSearchChange(value: string): void {
    this.templateSearchQuery.set(value);
    this.applyTemplateFilters();
  }

  toggleProjectFilter(): void {
    this.filterByProject.update(v => !v);
    this.applyTemplateFilters();
  }

  toggleCustomerFilter(): void {
    this.filterByCustomer.update(v => !v);
    this.applyTemplateFilters();
  }

  showMoreTemplates(): void {
    this.templateDisplayLimit.update(l => l + this.TEMPLATE_PAGE_SIZE);
  }

  // === Storage Bin Modal ===

  openStorageBinModal(itemIndex: number): void {
    this.storageBinModalItemIndex.set(itemIndex);
    this.storageBinSearchQuery.set('');
    this.showStorageBinModal.set(true);
  }

  closeStorageBinModal(): void {
    this.showStorageBinModal.set(false);
    this.storageBinModalItemIndex.set(-1);
  }

  selectStorageBin(bin: StorageBin): void {
    const index = this.storageBinModalItemIndex();
    if (index >= 0 && index < this.items().length) {
      this.items.update(items => {
        const updated = [...items];
        updated[index] = {
          ...updated[index],
          storageBinId: bin.id,
          storageBinLabel: bin.name
        };
        return updated;
      });
    }
    this.closeStorageBinModal();
  }

  filteredStorageBins(): StorageBin[] {
    const query = this.storageBinSearchQuery().toLowerCase();
    if (!query.trim()) {
      return this.storageBins;
    }
    return this.storageBins.filter(bin =>
      bin.name.toLowerCase().includes(query)
    );
  }

  onStorageBinSearchChange(value: string): void {
    this.storageBinSearchQuery.set(value);
  }

  // === Item Management ===

  updateItemOwner(index: number, owner: 'BENTOMAX' | 'CUSTOMER'): void {
    this.items.update(items => {
      const updated = [...items];
      updated[index] = { ...updated[index], owner };
      return updated;
    });
  }

  updateItemQuantity(index: number, quantity: number): void {
    const qty = Math.max(1, quantity);
    this.items.update(items => {
      const updated = [...items];
      updated[index] = { ...updated[index], quantityOrdered: qty };
      return updated;
    });
  }

  removeItem(index: number): void {
    this.items.update(items => items.filter((_, i) => i !== index));
  }

  addNewItem(): void {
    const hasCustomer = !!this.customerId();
    const newItem: ItemFormData = {
      id: 0, // Neue Position ohne ID
      displayDescription: '',
      customDescription: null,
      quantityOrdered: 1,
      serialNumbersRaw: null,
      articleTemplateId: null,
      articleTemplateName: null,
      articleTemplateSn: null,
      owner: hasCustomer ? 'CUSTOMER' : 'BENTOMAX',
      storageBinId: null,
      storageBinLabel: null
    };
    this.items.update(items => [...items, newItem]);
    // Öffne direkt das Template-Modal für die neue Position
    setTimeout(() => {
      this.openTemplateModal(this.items().length - 1);
    }, 100);
  }

  // === Admin User Modal ===

  openAdminUserModal(): void {
    if (!this.adminUsersLoaded) {
      this.loadAdminUsers();
    } else {
      this.updateAdminUserSelection();
    }
    this.showAdminUserModal.set(true);
  }

  closeAdminUserModal(): void {
    this.showAdminUserModal.set(false);
  }

  private loadAdminUsers(): void {
    this.userService.getAdminUsers().subscribe({
      next: (users) => {
        this.adminUsers = users.map(u => ({
          ...u,
          selected: this.isEmailAlreadySelected(u.email)
        }));
        this.adminUsersLoaded = true;
      },
      error: (err) => console.error('Fehler beim Laden der Admin-User:', err)
    });
  }

  private updateAdminUserSelection(): void {
    this.adminUsers = this.adminUsers.map(u => ({
      ...u,
      selected: this.isEmailAlreadySelected(u.email)
    }));
  }

  private isEmailAlreadySelected(email: string): boolean {
    const currentEmails = this.internalNotificationEmails();
    const emailList = currentEmails.split(';').map(e => e.trim().toLowerCase());
    return emailList.includes(email.toLowerCase());
  }

  toggleAdminUser(user: { id: number; name: string; email: string; selected: boolean }): void {
    user.selected = !user.selected;
  }

  applyAdminUserSelection(): void {
    const currentEmails = this.internalNotificationEmails();
    const emailSet = new Set(
      currentEmails.split(';').map(e => e.trim().toLowerCase()).filter(e => e)
    );

    this.adminUsers.forEach(user => {
      const emailLower = user.email.toLowerCase();
      if (user.selected) {
        emailSet.add(user.email);
      } else {
        emailSet.delete(emailLower);
      }
    });

    this.internalNotificationEmails.set(Array.from(emailSet).join('; '));
    this.closeAdminUserModal();
  }

  // === Helper ===

  getProjectName(): string {
    const id = this.projectId();
    if (!id) return '-';
    const project = this.projects.find(p => p.id === id);
    return project?.name || '-';
  }

  getCustomerName(): string {
    const id = this.customerId();
    if (!id) return '-';
    const customer = this.customers.find(c => c.id === id);
    return customer?.name || '-';
  }

  // === Date Handlers ===

  onExpectedFromChange(value: string): void {
    this.expectedFrom.set(value ? new Date(value) : null);
  }

  onExpectedUntilChange(value: string): void {
    this.expectedUntil.set(value ? new Date(value) : null);
  }

  getHandlingInstructionLabel(): string {
    const value = this.handlingInstruction();
    if (!value) return '-';
    const option = this.handlingInstructionOptions.find(o => o.value === value);
    return option ? option.label : value;
  }

  getDeliveryNotePdfUrl(filename: string): string {
    return this.expectedDeliveryService.getDeliveryNotePdfUrl(this.delivery.id, filename);
  }

  private formatDate(date: Date | null): string | null {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // === Helper for SN warnings ===

  getMissingSnCount(item: ItemFormData): number {
    if (!item.articleTemplateSn) return 0;
    const snCount = item.serialNumbersRaw?.length || 0;
    return Math.max(0, item.quantityOrdered - snCount);
  }

  // === Submit ===

  submit(): void {
    if (!this.canApprove()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    const itemsParams: ReleaseItemParams[] = this.items().map(item => ({
      id: item.id,
      article_template_id: item.articleTemplateId!,
      quantity_ordered: item.quantityOrdered,
      owner: item.owner,
      storage_bin_id: item.storageBinId!
    }));

    const params: ReleaseWithReceiptsParams = {
      handling_instruction: this.handlingInstruction(),
      order_nr: this.orderNr() || undefined,
      ticket_id: this.foundTicketId(),
      project_id: this.projectId(),
      customer_id: this.customerId(),
      expected_from: this.formatDate(this.expectedFrom()),
      expected_until: this.formatDate(this.expectedUntil()),
      internal_notification_emails: this.internalNotificationEmails(),
      external_notification_emails: this.externalNotificationEmails(),
      packaging_type: this.packagingType() as 'PALLET' | 'PACKAGE' | null || null,
      notes: this.notes(),
      items: itemsParams
    };

    this.expectedDeliveryService.releaseWithReceipts(this.delivery.id, params).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);
        this.approved.emit(response.expected_delivery);
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(err.error?.error || 'Fehler beim Freigeben der Lieferung.');
        console.error('Fehler:', err);
      }
    });
  }
}
